import Footer from '@features/auth/components/Footer'
import { useAuthSession } from '@features/auth'
import SidebarLayout from '@shared/components/SidebarMenu'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

type BusyCard = {
  card: HTMLElement
  requestIds: number[]
  action: 'execute' | 'export' | 'button'
  status: 'loading' | 'error'
  slow: boolean
}

export function AppShell() {
  const session = useAuthSession()
  const [busyCards, setBusyCards] = useState<BusyCard[]>([])
  const disabledButtons = useRef(new Map<HTMLButtonElement, boolean>())

  useEffect(() => {
    const trackAction = (event: MouseEvent) => {
      const button = (event.target as HTMLElement | null)?.closest('button')
      if (!button || button.classList.contains('vcc-btn--reset')) return

      const card = button.closest<HTMLElement>('.company-details-search-card')
      if (!card) return

      window.__vccBusyActionContext = {
        card,
        action: button.classList.contains('vcc-btn--export')
          ? 'export'
          : button.classList.contains('vcc-btn--execute')
            ? 'execute'
            : 'button',
        startedAt: Date.now(),
      }
    }

    const startRequest = (event: Event) => {
      const { requestId, card, action } = (event as CustomEvent<{
        requestId: number
        card: HTMLElement
        action: BusyCard['action']
      }>).detail

      if (!card?.isConnected) return
      setBusyCards((current) => {
        const existing = current.find((entry) => entry.card === card)
        if (existing) {
          return current.map((entry) =>
            entry === existing
              ? { ...entry, requestIds: [...entry.requestIds, requestId], action, status: 'loading', slow: false }
              : entry,
          )
        }
        window.setTimeout(() => {
          setBusyCards((active) =>
            active.map((entry) =>
              entry.card === card && entry.status === 'loading'
                ? { ...entry, slow: true }
                : entry,
            ),
          )
        }, 15000)
        return [...current, { card, requestIds: [requestId], action, status: 'loading', slow: false }]
      })
    }

    const endRequest = (event: Event) => {
      const { requestId, failed } = (event as CustomEvent<{ requestId: number; failed: boolean }>).detail
      setBusyCards((current) =>
        current.flatMap((entry) => {
          if (!entry.requestIds.includes(requestId)) return [entry]

          const requestIds = entry.requestIds.filter((id) => id !== requestId)
          if (requestIds.length > 0) return [{ ...entry, requestIds }]
          if (!failed) return []

          window.setTimeout(() => {
            setBusyCards((active) =>
              active.filter((activeEntry) => activeEntry.card !== entry.card || activeEntry.status !== 'error'),
            )
          }, 2500)
          return [{ ...entry, requestIds, status: 'error' }]
        }),
      )
    }

    document.addEventListener('click', trackAction, true)
    window.addEventListener('vcc-api-request-start', startRequest)
    window.addEventListener('vcc-api-request-end', endRequest)
    return () => {
      document.removeEventListener('click', trackAction, true)
      window.removeEventListener('vcc-api-request-start', startRequest)
      window.removeEventListener('vcc-api-request-end', endRequest)
    }
  }, [])

  useEffect(() => {
    disabledButtons.current.forEach((wasDisabled, button) => {
      button.disabled = wasDisabled
    })
    disabledButtons.current.clear()

    busyCards.forEach(({ card }) => {
      card.classList.add('vcc-card--busy')
      card
        .querySelectorAll<HTMLButtonElement>(
          'button.vcc-btn--execute, button.vcc-btn--export, button.vcc-btn--reset',
        )
        .forEach((button) => {
          disabledButtons.current.set(button, button.disabled)
          button.disabled = true
        })
    })

    return () => {
      busyCards.forEach(({ card }) => card.classList.remove('vcc-card--busy'))
      disabledButtons.current.forEach((wasDisabled, button) => {
        button.disabled = wasDisabled
      })
      disabledButtons.current.clear()
    }
  }, [busyCards])

  return (
    <>
      <div className="app-shell vcc-app">
        <div className="app-shell__body">
          <SidebarLayout menus={session?.menus ?? []} />
        </div>
        <Footer />
      </div>
      {busyCards.map(({ card, action, status, slow }) =>
        card.isConnected
          ? createPortal(
              <div className="vcc-card-busy-overlay" role="status" aria-live="polite">
                <span className="vcc-card-busy-overlay__spinner" aria-hidden="true" />
                <span>
                  {status === 'error'
                    ? 'Unable to complete the request.'
                    : action === 'export'
                      ? 'Exporting…'
                      : 'Loading results…'}
                </span>
                {status === 'loading' && slow && <small>Still working…</small>}
              </div>,
              card,
            )
          : null,
      )}
    </>
  )
}
